import { TenantType } from '@xeetr/types/enums'
import { ViewEmployee } from '@xeetr/types/models'
import { useQuery } from '@tanstack/react-query'
import { useContext } from 'react'

import { ActiveTenantContext } from '../context/common/Context'
import { queryKeys } from '../lib/queryKeys'
import { EmployeeService } from '../services/Employee.service'
import { useTenantRequiredService } from './common/useServiceFactory'

interface UseEmployeesReturn {
  employees: ViewEmployee[]
  isLoading: boolean
}

/**
 * Hook to access tenant employees via dedicated API endpoint.
 *
 * For employee absences, use `useAbsences()` hook instead.
 *
 * Phase 2: Uses dedicated /api/v3/service/management/employees endpoint
 * for independent caching and optimized payload size.
 *
 * @example
 * ```tsx
 * const { employees, isLoading } = useEmployees()
 * employees.map(employee => ...)
 * ```
 */
export const useEmployees = (): UseEmployeesReturn => {
  const { tenantId, tenant } = useContext(ActiveTenantContext)
  const enabled = !!tenantId && tenant?.type === TenantType.SERVICE

  const getService = useTenantRequiredService(EmployeeService)

  const { data: employees = [], isLoading } = useQuery({
    queryKey: queryKeys.employees.all(tenantId),
    queryFn: async () => {
      const service = await getService()
      return service.getEmployees()
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - employees don't change frequently
  })

  return { employees, isLoading: enabled ? isLoading : false }
}
